import React, { useState, useEffect, useCallback, useMemo } from "react";
import moment from "moment-timezone";
import axios from "axios";
import {
  Modal,
  Box,
  Backdrop,
  Fade,
  useMediaQuery,
  Paper,
  IconButton,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import Grid from "@mui/material/Grid";
import ScheduleModalSidebar from "@/app/components/modals/schedule/ScheduleModalSidebar";
import ScheduleModalCalendar from "@/app/components/modals/schedule/ScheduleModalCalendar";
import ScheduleModalActions from "@/app/components/modals/schedule/ScheduleModalActions";
import { calculateFees } from "@/lib/helpers/feeCalculator";
import AddPaymentModal from "@/app/components/modals/verify/AddPaymentModal";
import { TimezonedTime } from "@/app/components/TimezonedTime";
import { useTimezone } from "@/app/utils/TimezoneContext";
import { useDispatch, useSelector } from "react-redux";
import {
  showScheduledConfirmation,
  setMeeting,
  hideRescheduleDialog,
} from "@/store/apps/meetings/MeetingDataSlice";
import { rescheduleMeetingAdvancedThunk } from "@/store/apps/meetings/MeetingThunks";
import {
  checkDayAvailability as sharedCheckDayAvailability,
  generateTimeSlots as sharedGenerateTimeSlots,
  createMeetingsByDateMap,
  isDayBlocked as sharedIsDayBlocked,
} from "@/app/components/modals/schedule/schedulingRules";
import { track } from "@/lib/analytics/track";
import { EVENTS } from "@/lib/analytics/events";

const VERIFY_CARD_URL = `${process.env.NEXT_PUBLIC_APP_URL}/api/user/is-card-verified`;
const feeConfig = process.env.NEXT_PUBLIC_SEEKER_SERVICE_FEE;

const ScheduleModal = ({
  open,
  handleClose,
  content,
  isRescheduleMode = false,
  rescheduleData = null,
}) => {
  const { timezone } = useTimezone();
  const dispatch = useDispatch();
  const currentMeetings = useSelector((state) => state.meetings.data);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { timezone: userTimezone } = useTimezone();
  const [existingMeetings, setExistingMeetings] = useState([]);

  const [selectedOption, setSelectedOption] = useState("");
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [saveButtonText, setSaveButtonText] = useState(
    isRescheduleMode ? "Reschedule meeting" : "Book now",
  );
  const [isLoading, setIsLoading] = useState(false);
  const [dayOffset, setDayOffset] = useState(0);
  const [currentMonth, setCurrentMonth] = useState(moment().startOf("month"));
  const [selectedMonthYear, setSelectedMonthYear] = useState(
    moment().format("MMMM YYYY"),
  );
  const [selectedDay, setSelectedDay] = useState(moment().format("DD-MM-YYYY"));
  const [selectedTime, setSelectedTime] = useState(null);

  // Determine content source based on mode
  const meetingContent = isRescheduleMode
    ? rescheduleData?.meetingData
    : content;
  const requestId = isRescheduleMode
    ? rescheduleData?.meetingId
    : content?.request_id;
  const sharerId = isRescheduleMode
    ? meetingContent?.sharer?.id
    : content?.sharer_id;

  const [availability, setAvailability] = useState({
    times: [],
    window: 0,
    end_date: null,
    available: false,
    blocked_periods: [],
  });

  const [openAddPaymentModal, setOpenAddPaymentModal] = useState(false);
  const [estimatedTax, setEstimatedTax] = useState(null);
  const [billingPreview, setBillingPreview] = useState(null);

  const isSessionFree = useMemo(() => {
    return meetingContent?.session?.session_rate === 0;
  }, [meetingContent?.session?.session_rate]);

  const [actualCardVerified, setActualCardVerified] = useState(false);

  const isCardVerified = useMemo(() => {
    return isSessionFree || actualCardVerified || isRescheduleMode; // No payment needed for reschedule
  }, [isSessionFree, actualCardVerified, isRescheduleMode]);

  const checkCardVerification = useCallback(() => {
    if (isSessionFree || isRescheduleMode) {
      setActualCardVerified(false);
      return;
    }

    axios
      .get(VERIFY_CARD_URL, { withCredentials: true })
      .then((res) => {
        setActualCardVerified(res.data.verified);
      })
      .catch((err) => {
        console.error("Error verifying card:", err);
        setActualCardVerified(false);
      });
  }, [isSessionFree, isRescheduleMode]);

  useEffect(() => {
    if (open) {
      checkCardVerification();
      setSaveButtonText(isRescheduleMode ? "Reschedule meeting" : "Book now");
      // Analytics: booking (or reschedule) flow started.
      track(
        isRescheduleMode ? EVENTS.MEETING_RESCHEDULED : EVENTS.BOOKING_STARTED,
        {
          meeting_id: sharerId,
          is_reschedule: Boolean(isRescheduleMode),
        },
      );
    }
  }, [open, checkCardVerification, isRescheduleMode, sharerId]);

  const openVerifyCardModal = () => {
    if (isSessionFree || isRescheduleMode) {
      return;
    }
    setOpenAddPaymentModal(true);
  };

  const handleAddPaymentModalClose = () => {
    setOpenAddPaymentModal(false);
    if (!isSessionFree && !isRescheduleMode) {
      checkCardVerification();
    }
  };

  const [isAvailabilityLoading, setIsAvailabilityLoading] = useState(true);
  const [error, setError] = useState(null);

  const schedulingRules = useMemo(() => {
    return availability?.scheduling_rules || {};
  }, [availability?.scheduling_rules]);

  const meetingsByDate = useMemo(() => {
    return createMeetingsByDateMap(existingMeetings);
  }, [existingMeetings]);

  const timeSlots = useMemo(() => {
    return sharedGenerateTimeSlots(
      availability,
      selectedDay,
      meetingContent?.session?.duration,
      timezone,
      schedulingRules,
      existingMeetings,
    );
  }, [
    availability,
    selectedDay,
    meetingContent?.session?.duration,
    timezone,
    schedulingRules,
    existingMeetings,
  ]);

  const isBeforeEndDate = useCallback(
    (date) => {
      let endDate;
      if (availability.window) {
        endDate = moment()
          .tz(userTimezone)
          .add(availability.window, "days");
      } else {
        endDate = availability.end_date?._seconds
          ? moment.unix(availability.end_date._seconds)
          : moment(availability.end_date);
      }
      return endDate.isAfter(date);
    },
    [availability.window, availability.end_date, userTimezone],
  );

  const formatSelectedDateTime = useCallback(() => {
    if (!selectedDay || !selectedTime) return "";

    const combinedDateTime = moment
      .tz(selectedDay, "DD-MM-YYYY", userTimezone)
      .hour(selectedTime.hour())
      .minute(selectedTime.minute())
      .second(0);

    return (
      <span>
        <TimezonedTime
          time={combinedDateTime}
          format="ddd MMM D, h:mm A z"
          sx={{ fontSize: "0.9rem" }}
        />
      </span>
    );
  }, [selectedDay, selectedTime, userTimezone]);

  const monthYearOptions = useMemo(() => {
    const options = [];
    const current = moment();
    let endDate;
    if (availability.window) {
      endDate = moment()
        .tz(userTimezone)
        .add(availability.window, "days");
    } else {
      endDate = availability.end_date?._seconds
        ? moment.unix(availability.end_date._seconds)
        : moment(availability.end_date);
    }
    const monthsDiff = endDate.diff(current, "months") + 2;
    for (let i = 0; i < monthsDiff; i++) {
      options.push(
        current
          .clone()
          .add(i, "months")
          .format("MMMM YYYY"),
      );
    }
    return options;
  }, [availability.window, availability.end_date, userTimezone]);

  const isDayBlocked = useCallback(
    (day) => {
      return sharedIsDayBlocked(
        day,
        schedulingRules,
        availability,
        meetingsByDate,
      );
    },
    [schedulingRules, availability, meetingsByDate],
  );

  const handleDaySelect = (day) => {
    if (isDayBlocked(day)) {
      return;
    }

    setSelectedDay(day.format("DD-MM-YYYY"));
    setSelectedTime(null);
  };

  const handleTimeSelect = useCallback(
    (time) => {
      const timeInUserTz = time.tz(userTimezone);
      setSelectedTime(timeInUserTz);
    },
    [userTimezone],
  );

  const isToday = moment(selectedDay, "DD-MM-YYYY").isSame(moment(), "day");
  const currentTime = moment();

  const handleMonthYearChange = (event) => {
    const selected = event.target.value;
    setSelectedMonthYear(selected);
    const selectedMonth = moment(selected, "MMMM YYYY").startOf("month");
    const today = moment().startOf("day");
    setDayOffset(selectedMonth.diff(today, "days"));
  };

  const confirmSchedule = useCallback(async () => {
    if (!requestId || !selectedTime || !selectedDay || !sharerId) {
      console.error("Missing required scheduling data");
      return;
    }

    setIsLoading(true);
    setSaveButtonText(isRescheduleMode ? "Rescheduling..." : "Booking...");

    try {
      const selectedDate = moment.tz(selectedDay, "DD-MM-YYYY", userTimezone);
      const hours = selectedTime.hour();
      const minutes = selectedTime.minute();
      selectedDate.set({ hour: hours, minute: minutes });
      const scheduledDateTime = selectedDate.utc().format();

      if (isRescheduleMode) {
        // Handle reschedule
        const result = await dispatch(
          rescheduleMeetingAdvancedThunk(
            requestId,
            scheduledDateTime,
            rescheduleData?.userRole || "seeker",
          ),
        ).unwrap();

        const currentMeeting = currentMeetings.find((m) => m.id === requestId);
        if (currentMeeting) {
          const newRescheduleNumber =
            result.rescheduleNumber ||
            (currentMeeting.session?.reschedule_number || 0) + 1;

          dispatch(
            setMeeting({
              ...currentMeeting,
              meeting_time: scheduledDateTime,
              session: {
                ...currentMeeting.session,
                meeting_time: scheduledDateTime,
                reschedule_number: newRescheduleNumber,
              },
              status_log: [
                ...(currentMeeting.status_log || []),
                {
                  status: "rescheduled",
                  time: new Date().toISOString(),
                  meeting_time: scheduledDateTime,
                  by: rescheduleData?.userRole,
                  reschedule_number: newRescheduleNumber,
                  role: rescheduleData?.userRole,
                },
              ],
            }),
          );
        }

        dispatch(hideRescheduleDialog());
        handleClose();
      } else {
        const response = await fetch("/api/meeting/schedule", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            requestId,
            scheduledDateTime,
            sharerId,
          }),
        });

        const result = await response.json();

        if (result.success) {
          const currentMeeting = currentMeetings.find(
            (m) => m.id === requestId,
          );
          const now = new Date().toISOString();

          if (currentMeeting) {
            dispatch(
              setMeeting({
                ...currentMeeting,
                status: "scheduled",
                status_time: now,
                meeting_time: scheduledDateTime,
                session: {
                  ...currentMeeting.session,
                  meeting_time: scheduledDateTime,
                },
                status_log: [
                  ...(currentMeeting.status_log || []),
                  {
                    status: "scheduled",
                    time: now,
                    meeting_time: scheduledDateTime,
                  },
                ],
              }),
            );
          }

          const meetingDetails = {
            ...currentMeeting,
            dateTime: formatSelectedDateTime(),
            duration: meetingContent?.session?.duration || 60,
            sharerName: meetingContent?.sharer?.given_name,
            meeting_time: scheduledDateTime,
          };

          dispatch(showScheduledConfirmation(meetingDetails));
          handleClose();
        } else {
          console.error("Failed to schedule meeting:", result.message);
        }
      }
    } catch (error) {
      console.error(
        `Error ${isRescheduleMode ? "rescheduling" : "scheduling"} meeting:`,
        error,
      );
    } finally {
      setIsLoading(false);
      setSaveButtonText(isRescheduleMode ? "Reschedule meeting" : "Book now");
    }
  }, [
    requestId,
    handleClose,
    selectedDay,
    selectedTime,
    sharerId,
    userTimezone,
    formatSelectedDateTime,
    meetingContent,
    dispatch,
    currentMeetings,
    isRescheduleMode,
    rescheduleData,
  ]);

  const handlePrevDays = () => {
    const newOffset = dayOffset - 7;
    const currentDate = moment().add(newOffset, "days");
    setCurrentMonth(currentDate.clone().startOf("month"));
    setDayOffset(newOffset);
  };

  const handleNextDays = () => {
    const newOffset = dayOffset + 7;
    const currentDate = moment().add(newOffset, "days");
    if (!isBeforeEndDate(currentDate)) return;
    setCurrentMonth(currentDate.clone().startOf("month"));
    setDayOffset(newOffset);
  };

  const daysToShow = useMemo(() => {
    const tz = userTimezone || moment.tz.guess();
    const days = Array.from({ length: 7 }, (_, i) => {
      const day = moment
        .tz(tz)
        .add(dayOffset + i, "days")
        .clone();
      return day;
    });
    return days;
  }, [dayOffset, userTimezone]);

  const checkDayAvailability = useCallback(
    (day) =>
      sharedCheckDayAvailability(
        day,
        availability,
        meetingContent?.session?.duration,
        userTimezone,
        schedulingRules,
        existingMeetings,
        meetingsByDate,
      ),
    [
      availability,
      meetingContent?.session?.duration,
      userTimezone,
      schedulingRules,
      existingMeetings,
      meetingsByDate,
    ],
  );

  const handleFindFirstAvailableDay = useCallback(() => {
    const maxDaysToCheck = 30;

    for (let i = 0; i < maxDaysToCheck; i++) {
      const checkDay = moment().add(i, "days");

      if (checkDayAvailability(checkDay)) {
        setSelectedDay(checkDay.format("DD-MM-YYYY"));
        setDayOffset(i);
        return;
      }
    }
  }, [checkDayAvailability]);

  useEffect(() => {
    const fetchSharerAvailability = async () => {
      if (!sharerId) return;
      setIsAvailabilityLoading(true);
      setError(null);
      try {
        const GET_AVAILABILITY_URL = `${process.env.NEXT_PUBLIC_APP_URL}/api/availability/get-availability`;
        const response = await axios.get(GET_AVAILABILITY_URL, {
          params: { userId: sharerId },
        });
        if (response.data && response.data.times) {
          setAvailability(response.data);
        } else {
          throw new Error("No availability data received");
        }
      } catch (err) {
        if (err.response?.status === 404) {
          setAvailability({
            times: [],
            window: 0,
            end_date: null,
            available: false,
            blocked_periods: [],
          });
          setError("No availability set by the sharer.");
        } else {
          console.error("Error fetching availability:", err);
          setError(err.response?.data?.error || err.message);
        }
      } finally {
        setIsAvailabilityLoading(false);
      }
    };
    fetchSharerAvailability();
  }, [sharerId]);

  useEffect(() => {
    const fetchExistingMeetings = async () => {
      if (!sharerId || !open) return;

      try {
        const response = await axios.get(
          `${process.env.NEXT_PUBLIC_APP_URL}/api/meeting/sharer/${sharerId}`,
          { withCredentials: true },
        );
        setExistingMeetings(response.data.meetings || []);
      } catch (error) {
        console.error("Error fetching existing meetings:", error);
        setExistingMeetings([]);
        console.warn("Continuing without meeting conflict checks");
      }
    };

    fetchExistingMeetings();
  }, [sharerId, open]);

  useEffect(() => {
    setSelectedMonthYear(currentMonth.format("MMMM YYYY"));
  }, [currentMonth]);

  useEffect(() => {
    setIsSaveDisabled(!selectedDay || !selectedTime);
  }, [selectedDay, selectedTime, selectedOption]);

  const serviceFee = useMemo(() => {
    if (!open || isRescheduleMode) return null; // No fees for reschedule
    const fees = calculateFees(
      meetingContent?.session?.session_rate,
      feeConfig,
    );
    return fees.seekerServiceFee;
  }, [meetingContent, open, isRescheduleMode]);

  useEffect(() => {
    async function fetchTax() {
      if (open && feeConfig && !isRescheduleMode) {
        try {
          const previewResponse = await axios.post("/api/billing/preview", {
            sessionRate: Number(meetingContent?.session?.session_rate || 0),
          });
          if (previewResponse.data?.success && previewResponse.data?.preview) {
            setBillingPreview(previewResponse.data.preview);
            setEstimatedTax(previewResponse.data.preview.taxAmount || 0);
          } else {
            throw new Error("Invalid billing preview response");
          }
        } catch (error) {
          console.error("Error fetching billing preview:", error);
          setBillingPreview(null);
          setEstimatedTax(0);
        }
      }
    }

    if (open && !isRescheduleMode) {
      fetchTax();
    } else {
      setEstimatedTax(null);
      setBillingPreview(null);
    }
  }, [serviceFee, meetingContent, open, isRescheduleMode]);

  const showNextArrow = isBeforeEndDate(moment().add(dayOffset + 7, "days"));
  const showBackArrow = dayOffset > 0;
  const cutoffDate = moment();

  useEffect(() => {
    if (open) {
      document.body.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
    }

    return () => {
      document.body.classList.remove("modal-open");
    };
  }, [open]);

  useEffect(() => {
    if (open && availability?.available && !isAvailabilityLoading) {
      const currentDayHasAvailability = selectedDay
        ? checkDayAvailability(moment(selectedDay, "DD-MM-YYYY"))
        : false;

      if (!selectedDay || !currentDayHasAvailability) {
        handleFindFirstAvailableDay();
      }
    }
  }, [
    open,
    availability?.available,
    isAvailabilityLoading,
    checkDayAvailability,
    selectedDay,
    handleFindFirstAvailableDay,
  ]);

  useEffect(() => {
    if (open) {
      setSelectedDay(null);
      setSelectedTime(null);
      setDayOffset(0);
    }
  }, [open]);

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        closeAfterTransition
        disableAutoFocus
        disableEnforceFocus
        aria-labelledby="schedule-modal-title"
        aria-describedby="schedule-modal-description"
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            style: {
              backgroundColor: "rgba(0, 0, 0, 0.15)",
              backdropFilter: "blur(8px)",
            },
          },
        }}
      >
        <Fade in={open}>
          <Paper
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "100%", sm: "calc(100vw - 48px)" },
              maxWidth: 1000,
              height: { xs: "100%", sm: "auto" },
              maxHeight: { xs: "100%", sm: "90vh" },
              borderRadius: { xs: "0!important", sm: "10px!important" },
              boxShadow: 24,
              overflow: "hidden",
              boxSizing: "border-box",
              paddingTop: { xs: "env(safe-area-inset-top)", sm: 0 },
              paddingBottom: { xs: "env(safe-area-inset-bottom)", sm: 0 },
              display: "flex",
              flexDirection: "column",
              outline: "none",
            }}
          >
            {/* Close button */}
            <IconButton
              onClick={handleClose}
              aria-label="Close scheduling"
              sx={{
                position: "absolute",
                top: {
                  xs: "calc(env(safe-area-inset-top) + 8px)",
                  sm: 16,
                  md: 16,
                },
                right: { xs: 8, sm: 16, md: 16 },
                zIndex: 1000,
                backgroundColor: "background.paper",
                "&:hover": {
                  backgroundColor: "action.hover",
                },
              }}
            >
              <CloseIcon />
            </IconButton>

            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                pt: {
                  xs: 3,
                  sm: 4,
                  md: 4,
                  lg: 4,
                },
                px: {
                  xs: 3,
                  md: 4,
                  lg: 4,
                },
                pb: {
                  xs: 2,
                  sm: 3,
                  md: 3,
                  lg: 3,
                },
                scrollbarWidth: "thin",
                scrollbarColor: `${theme.palette.grey[400]} transparent`,
                "&::-webkit-scrollbar": {
                  width: 8,
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: theme.palette.grey[400],
                  borderRadius: 2,
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "transparent",
                },
              }}
            >
              <Typography
                id="schedule-modal-title"
                variant="h5"
                sx={{
                  fontWeight: 600,
                  fontSize: "1.625rem",
                  lineHeight: 1.15,
                  mb: 1,
                  pr: 5,
                }}
              >
                {isRescheduleMode ? "Reschedule meeting" : "Schedule meeting"}
              </Typography>
              <Typography
                id="schedule-modal-description"
                variant="body2"
                sx={{ color: "text.secondary", mb: 3 }}
              >
                {isRescheduleMode
                  ? "Choose a new date and time for your meeting."
                  : "Choose a date and time for your meeting."}
              </Typography>

              {/* Main content */}
              <Grid container spacing={4} columns={12}>
                {/* Calendar */}
                <Grid
                  size={{
                    xs: 12,
                    sm: 12,
                    md: 8,
                    lg: 8,
                    xl: 8,
                  }}
                  sx={{
                    order: 2,
                  }}
                >
                  <ScheduleModalCalendar
                    isAvailabilityLoading={isAvailabilityLoading}
                    availability={availability}
                    selectedMonthYear={selectedMonthYear}
                    handleMonthYearChange={handleMonthYearChange}
                    monthYearOptions={monthYearOptions}
                    handlePrevDays={handlePrevDays}
                    showBackArrow={showBackArrow}
                    showNextArrow={showNextArrow}
                    daysToShow={daysToShow}
                    cutoffDate={cutoffDate}
                    handleDaySelect={handleDaySelect}
                    selectedDay={selectedDay}
                    theme={theme}
                    handleNextDays={handleNextDays}
                    timeSlots={timeSlots}
                    selectedTime={selectedTime}
                    currentTime={currentTime}
                    isToday={isToday}
                    handleTimeSelect={handleTimeSelect}
                    isMobile={isMobile}
                    meetingData={meetingContent}
                    userTimezone={userTimezone}
                    isBeforeEndDate={isBeforeEndDate}
                    existingMeetings={existingMeetings}
                    checkDayAvailability={checkDayAvailability}
                  />
                </Grid>

                {/* Sidebar */}
                <Grid
                  size={{
                    xs: 12,
                    sm: 12,
                    md: 4,
                    lg: 4,
                    xl: 4,
                  }}
                  sx={{
                    order: 1,
                    borderRight: { md: "1px solid" },
                    borderColor: "divider",
                    pr: { md: 3 },
                  }}
                >
                  <ScheduleModalSidebar
                    meetingData={meetingContent}
                    feeConfig={isRescheduleMode ? null : feeConfig} // No fees for reschedule
                    estimatedTax={isRescheduleMode ? null : estimatedTax} // No tax for reschedule
                    billingPreview={billingPreview}
                    isRescheduleMode={isRescheduleMode}
                  />
                </Grid>
              </Grid>
            </Box>

            <Box
              sx={{
                position: "relative",
                flexShrink: 0,
                px: {
                  xs: 3,
                  sm: 3,
                  md: 4,
                  lg: 4,
                },
                py: { xs: 1.5, sm: 2 },
                pb: {
                  xs: "max(12px, env(safe-area-inset-bottom))",
                  sm: 2,
                },
                borderTop: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.background.paper,
                zIndex: 20,
              }}
            >
              <ScheduleModalActions
                selectedDay={selectedDay}
                selectedTime={selectedTime}
                formatSelectedDateTime={formatSelectedDateTime}
                availability={availability}
                confirmSchedule={confirmSchedule}
                isSaveDisabled={isSaveDisabled}
                isLoading={isLoading}
                saveButtonText={saveButtonText}
                theme={theme}
                isCardVerified={isCardVerified}
                openVerifyCardModal={openVerifyCardModal}
                fullWidthMobile={isMobile}
                userTimezone={userTimezone}
                meetingData={meetingContent}
                isRescheduleMode={isRescheduleMode}
              />
            </Box>
          </Paper>
        </Fade>
      </Modal>
      {!isRescheduleMode && (
        <AddPaymentModal
          open={openAddPaymentModal}
          handleClose={handleAddPaymentModalClose}
        />
      )}
    </>
  );
};

export default ScheduleModal;
